import {
  formatCurrencyNgn,
  formatMonthsLabel,
  normalizeOrgPaymentAdvicePayload,
  orgPaymentAdviceFilename,
} from './orgPaymentAdviceHelpers';

const AMAN_NAME = 'Aman Medicare';
const AMAN_TAGLINE = 'Healthcare Provider Network';
const REPORT_TITLE = 'Utilization Report';

const DEFAULT_COLUMNS = [
  { key: 'encounter_code', label: 'Encounter code' },
  { key: 'patient_name', label: 'Patient' },
  { key: 'insurance_number', label: 'Insurance Number' },
  { key: 'policy_name', label: 'Policy name' },
  { key: 'plan_name', label: 'Plan' },
  { key: 'facility_name', label: 'Facility' },
  { key: 'check_in_date', label: 'Check in date' },
  { key: 'request_category', label: 'Request Category' },
  { key: 'item_count', label: 'No of Items' },
  { key: 'approved_cost', label: 'Approved Cost' },
];

function resolveColumns(payload) {
  if (Array.isArray(payload?.columns) && payload.columns.length) {
    return payload.columns.map((col) => ({
      key: col.key,
      label: col.label || col.key,
    }));
  }
  return DEFAULT_COLUMNS;
}

function formatCellValue(key, value) {
  if (key === 'approved_cost') return formatCurrencyNgn(value);
  if (key === 'item_count' && value != null && value !== '') return String(value);
  return value == null || value === '' ? '' : String(value);
}

/**
 * Build flat table matrix from payload (prefers rows_flat from API).
 */
export function buildPaymentAdviceTable(payload) {
  const normalized = normalizeOrgPaymentAdvicePayload(payload);
  const columns = resolveColumns(normalized);
  const headers = columns.map((c) => c.label);
  const encounterKeys = new Set([
    'encounter_code',
    'patient_name',
    'insurance_number',
    'policy_name',
    'plan_name',
    'facility_name',
    'check_in_date',
  ]);

  if (Array.isArray(normalized?.rows_flat) && normalized.rows_flat.length) {
    const matrix = normalized.rows_flat.map((row) =>
      columns.map((col) => {
        if (!row.is_first_line_of_encounter && encounterKeys.has(col.key)) {
          return '';
        }
        return formatCellValue(col.key, row[col.key]);
      }),
    );
    return { headers, matrix, columns };
  }

  const matrix = [];
  (normalized?.rows || []).forEach((encounter) => {
    const lines = encounter.category_lines?.length
      ? encounter.category_lines
      : [{ request_category: '', item_count: '', approved_cost: '' }];

    lines.forEach((line, idx) => {
      matrix.push(
        columns.map((col) => {
          if (col.key === 'request_category') return formatCellValue(col.key, line.request_category);
          if (col.key === 'item_count') return formatCellValue(col.key, line.item_count);
          if (col.key === 'approved_cost') return formatCellValue(col.key, line.approved_cost);
          if (idx > 0 && encounterKeys.has(col.key)) return '';
          return formatCellValue(col.key, encounter[col.key]);
        }),
      );
    });
  });

  return { headers, matrix, columns };
}

function buildSummaryHeaderRows(payload) {
  const normalized = normalizeOrgPaymentAdvicePayload(payload);
  const orgName = normalized?.organization?.name || 'Organization';
  const summary = normalized?.summary || {};
  const monthsLabel = formatMonthsLabel(summary.months_covered);
  const segmentLabel = normalized?.scope?.claim_segment_label || normalized?.scope?.data_scope_label || 'Paid';
  const isPa = normalized?.scope?.data_scope === 'pa_approved';

  const lines = [
    `Total Enrollees: ${summary.total_enrollees ?? '—'}`,
    `Months: ${monthsLabel}`,
    `Claim segment: ${segmentLabel}`,
    `Total encounters: ${summary.total_encounters ?? '—'}`,
  ];

  if (isPa) {
    lines.splice(1, 0, `Total PA amount: ${formatCurrencyNgn(summary.total_pa_amount ?? summary.total_approved_pa)}`);
    if (summary.metrics_basis) {
      lines.push(`Metrics basis: ${String(summary.metrics_basis).replace(/_/g, ' ')}`);
    }
  } else {
    lines.splice(1, 0, `Total approved PA: ${formatCurrencyNgn(summary.total_approved_pa)}`);
    lines.push(`Total approved cost: ${formatCurrencyNgn(summary.total_approved_cost)}`);
  }

  return {
    title: `CONSUMPTION INFORMATION ${orgName.toUpperCase()}`,
    summaryLines: lines,
  };
}

function triggerBlobDownload(blob, filename) {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}

export async function downloadOrgPaymentAdviceXlsx(payload, filename) {
  const XLSX = await import('xlsx');
  const name = filename || orgPaymentAdviceFilename(payload, 'xlsx');
  const { title, summaryLines } = buildSummaryHeaderRows(payload);
  const { headers, matrix } = buildPaymentAdviceTable(payload);
  const generatedAt = payload?.generated_at
    ? new Date(payload.generated_at).toLocaleString()
    : new Date().toLocaleString();
  const exportedBy = payload?.generated_by?.display_name || '—';

  const coverRows = [
    [AMAN_NAME],
    [AMAN_TAGLINE],
    [],
    [REPORT_TITLE],
    [title],
    [],
    ...summaryLines.map((line) => [line]),
    [],
    ['Generated', generatedAt],
    ['Exported by', exportedBy],
    [],
    ['Confidential — For authorized internal use only.'],
  ];

  const coverSheet = XLSX.utils.aoa_to_sheet(coverRows);
  coverSheet['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } },
    { s: { r: 3, c: 0 }, e: { r: 3, c: 6 } },
    { s: { r: 4, c: 0 }, e: { r: 4, c: 6 } },
  ];
  coverSheet['!cols'] = [{ wch: 22 }, { wch: 52 }];

  const sheetRows = [
    [title],
    [],
    ...summaryLines.map((line) => [line]),
    [],
    headers,
    ...matrix,
  ];

  if (payload?.totals) {
    sheetRows.push([]);
    sheetRows.push([
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      'TOTAL',
      String(payload.totals.item_count ?? ''),
      formatCurrencyNgn(payload.totals.approved_cost),
    ]);
  }

  const dataSheet = XLSX.utils.aoa_to_sheet(sheetRows);
  const colCount = Math.max(headers.length, 2);
  dataSheet['!cols'] = Array.from({ length: colCount }, (_, colIdx) => {
    const maxLen = Math.max(
      String(headers[colIdx] ?? '').length,
      ...matrix.slice(0, 300).map((row) => String(row[colIdx] ?? '').length),
      colIdx === 0 ? title.length : 0,
    );
    return { wch: Math.min(44, Math.max(10, maxLen + 2)) };
  });
  dataSheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } }];
  const headerRowIndex = sheetRows.findIndex((row) => row === headers);
  if (headerRowIndex >= 0) {
    dataSheet['!freeze'] = {
      xSplit: 0,
      ySplit: headerRowIndex + 1,
      topLeftCell: `A${headerRowIndex + 2}`,
      activePane: 'bottomLeft',
    };
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, coverSheet, 'Cover');
  XLSX.utils.book_append_sheet(wb, dataSheet, 'Utilization Report');
  XLSX.writeFile(wb, name);
  return { success: true, filename: name };
}

export async function renderOrgPaymentAdviceFromPayload(payload, format, filename) {
  const normalized = normalizeOrgPaymentAdvicePayload(payload);
  if (format === 'pdf') {
    const { renderOrgPaymentAdvicePdf } = await import('./orgPaymentAdvicePdf');
    return renderOrgPaymentAdvicePdf(normalized, filename);
  }
  return downloadOrgPaymentAdviceXlsx(normalized, filename);
}

export async function renderOrgPaymentAdviceFromJob(jobId, format) {
  const lib = (await import('../../assets/utils/lib')).default;
  const { payload } = await lib.parseClaimsExportJobJson(jobId);
  const filename = orgPaymentAdviceFilename(payload, format === 'pdf' ? 'pdf' : 'xlsx');
  return renderOrgPaymentAdviceFromPayload(payload, format, filename);
}
